// frontend/src/content/professions/lawyers.js
//
// The /lawyers landing page, as data. Nothing here is styling or layout - see
// `schema.js` for the shape and `pages/ProfessionLanding.js` for the render.
//
// COPY PROVENANCE. Every visible string is one of:
//   (a) verbatim from the owner-approved content document
//       `docs/lawyers-page-concepts/content-proposal.html` (the A*/P*/G* ids in
//       the comments below are that document's own markers), or
//   (b) verbatim from a constant already shipping on the homepage, or
//   (c) NEW - and then it is ALSO listed in `PENDING_OWNER_APPROVAL` at the
//       bottom, so the owner approves it on the rendered page.
//
// SEO ROUND 2, APPROVED 2026-09-22 (owner decision 3). Five strings were added
// or replaced so the page answers its own head query in the places a crawler
// weighs first. They are approved copy, NOT pending, and each is marked
// "approved 2026-09-22" at its site:
//   1. `where.eyebrow`      - the query as a question, above the fold
//   2. `opportunity.h2`     - the only H2 that carries "לידים לעורכי דין"
//   3. `faq[0]`             - the head query, verbatim, as the first question
//   4. `faq[4]`             - the "bought lead vs identified opportunity" query
//   5. `seo.description`    - trimmed under the 160-char cap the schema now
//                             enforces (the untrimmed original survives as
//                             `jsonld.serviceDescription`, which has no cap)
// The shapes these five fill are the `{profession}` template documented in
// `schema.js` and validated there - the next profession page fills the same
// slots rather than inventing its own.
//
// CATALOGUE RULE (heroData.js): every `pro` label and every `fields` entry names
// a profession this product actually matches, so each one must exist in the
// professions catalogue. Verification notes are on `fields` below, including
// the PRE-LAUNCH CHECK against the PROD catalogue.
//
// Wording rules applied (CLAUDE.md): ASCII hyphen only, never an em/en dash;
// "סוכני AI", never "אוטומציה"; this is an SEO surface, so "לידים" appears
// alongside "לקוחות פוטנציאליים" and "הזדמנויות"; the first brand mention on
// the page (the hero sub) is "לידיפיי (Leadify AI)".

import { assertProfessionContent } from "./schema";

// -----------------------------------------------------------------------------
// Hero demo deck - six lawyer opportunities, one per practice area.
//
// Owner decision 2026-09-17: diverse specialties, no divorce tilt. The single
// family-law card the document approved (P9) lives in the OPPORTUNITY section
// below, so the page carries exactly one.
//
// Every `txt` is INVENTED demo content in the house style - a first-person post,
// a concrete situation, an area and a request for a lawyer or a recommendation,
// with no names and no phone numbers. All of them are listed in
// PENDING_OWNER_APPROVAL.
// -----------------------------------------------------------------------------
const HERO_LEADS = [
  {
    // Torts.
    pro: "עורך דין נזיקין וביטוח",
    grp: "פורסם ב: תושבי ראשון לציון והסביבה",
    txt: "נפצעתי בתאונת עבודה לפני חודש וחברת הביטוח דוחה את התביעה שלי, מישהו ממליץ על עורך דין נזיקין באזור?",
    city: "ראשון לציון",
    time: "לפני 3 דקות",
    score: 93,
  },
  {
    // Social security.
    pro: "עורך דין ביטוח לאומי",
    grp: "פורסם ב: המלצות בחיפה והקריות",
    txt: "הוועדה הרפואית של הביטוח הלאומי קבעה לי אחוזי נכות נמוכים מדי ואני רוצה לערער, אשמח להמלצה על עורך דין.",
    city: "חיפה",
    time: "לפני 6 דקות",
    score: 91,
  },
  {
    // Real estate.
    pro: "עורך דין מקרקעין",
    grp: "פורסם ב: קונים דירה במרכז",
    txt: "אנחנו קונים דירה ראשונה מקבלן וצריכים עורך דין מקרקעין שיעבור על חוזה הרכישה לפני החתימה, המלצות באזור?",
    city: "פתח תקווה",
    time: "לפני 9 דקות",
    score: 92,
  },
  {
    // Enduring power of attorney.
    pro: "עורך דין ייפוי כוח מתמשך",
    grp: "פורסם ב: מטפלים בהורים מבוגרים",
    txt: "אמא שלי בת 82 ורוצה לערוך ייפוי כוח מתמשך בזמן שהיא צלולה, מישהו מכיר עורך דין שמוסמך לזה באזור?",
    city: "רמת גן",
    time: "לפני 11 דקות",
    score: 90,
  },
  {
    // Notary.
    pro: "נוטריון",
    grp: 'פורסם ב: לומדים ועובדים בחו"ל',
    txt: 'אני צריכה תרגום נוטריוני של תעודת לידה ותעודת בגרות להגשה ללימודים בחו"ל, מישהו ממליץ על נוטריון באזור?',
    city: "תל אביב",
    time: "לפני 7 דקות",
    score: 89,
  },
  {
    // Insolvency.
    pro: "עורך דין חדלות פירעון",
    grp: "פורסם ב: כלכלת המשפחה - עוזרים אחד לשני",
    txt: "נכנסתי לחובות אחרי שהעסק שלי נסגר ואני שוקל הליך חדלות פירעון, מישהו מכיר עורך דין שילווה אותי בתהליך?",
    city: "אשדוד",
    time: "לפני 14 דקות",
    score: 88,
  },
];

// -----------------------------------------------------------------------------
// The professions the mesh names when a node ignites.
//
// CATALOGUE VERIFICATION (read-only SELECT against the DEV database,
// `professions.hebrew`, 2026-09-17): every label below exists as a catalogue
// row with its own `profession_key` (tort_attorney,
// national_insurance_attorney, real_estate_attorney,
// durable_power_of_attorney_lawyer, notary, traffic_lawyer,
// insolvency_bankruptcy_attorney, family_divorce_attorney).
//
// PRE-LAUNCH CHECK: verify each `pro` label is an ACTIVE row in the PROD
// professions table (dev has all lawyer rows inactive).
//
// `עו"ד דיני עבודה` - the homepage's own labour-law label - is deliberately
// ABSENT: it has no catalogue row, and one page may not mix the two naming
// styles. `עורך דין נכי צהל` exists in the catalogue but is also not used - the
// stored name is missing its punctuation and would read wrong in public copy.
//
// DECK LENGTH, RE-CHECKED 2026-09-22. The round-2 brief asked for 12-14 names
// so the shuffled mesh deck repeats less often. IT CANNOT BE DONE FROM THE
// CATALOGUE: a fresh read-only `SELECT hebrew, profession_key, is_active FROM
// professions` over the whole DEV table (51 rows) returns exactly NINE lawyer
// rows - the eight below plus `עורך דין נכי צהל`, still rejected for its
// missing punctuation. There is no twelfth name to add that would not be
// invented, and the catalogue rule ("never name a profession the product does
// not match") outranks the deck-length wish. Growing this deck is therefore a
// CATALOGUE job first (`/create-profession` for e.g. wills and probate,
// employment law, criminal defence, commercial contracts, medical negligence),
// and only then a content edit here. Meanwhile the repetition the brief was
// aiming at is addressed from the other side: the round-2 ignite cooldown plus
// the 6 s tag dwell cut the accepted-tag rate roughly fourfold, which lengthens
// the perceived cycle of an eight-name deck by about as much as 12 names would.
// -----------------------------------------------------------------------------
const HERO_FIELDS = [
  "עורך דין נזיקין וביטוח",
  "עורך דין ביטוח לאומי",
  "עורך דין מקרקעין",
  "עורך דין ייפוי כוח מתמשך",
  "נוטריון",
  "עורך דין תעבורה",
  "עורך דין חדלות פירעון",
  "עורך דין משפחה וגירושין",
];

const content = {
  slug: "lawyers",
  route: "/lawyers",

  seo: {
    // Owner's choice 2026-09-22, replacing P22 / G11's
    // "לידים לעורכי דין מקבוצות פייסבוק | Leadify AI". That title was a near
    // duplicate of the hub article's own metaTitle
    // ("לידים לעורכי דין מקבוצות פייסבוק: מה מותר ומה עובד - 2026"), so the two
    // URLs competed for one query. The landing now leads on what it SELLS
    // ("קבלו פניות חמות") while keeping the head query first; the article keeps
    // the informational framing. Head query first, brand last, 47 chars.
    title: "לידים לעורכי דין - קבלו פניות חמות | Leadify AI",
    // P23 / G12, TRIMMED and approved 2026-09-22 (owner decision 3). The
    // original ran 169 characters, so Google truncated the closing promise in
    // the SERP; this cut is 137 and clears the 160-char cap `schema.js` now
    // enforces. The head query still opens it. The brand gloss moves to the
    // hero sub, which remains the page's first on-page mention.
    description:
      "לידים לעורכי דין: סוכני AI של לידיפיי סורקים קבוצות פייסבוק 24/7, מזהים מי מחפש עורך דין בתחום ובאזור שלכם ושולחים את ההזדמנות תוך שניות.",
    canonical: "/lawyers",
  },

  jsonld: {
    // NEW (machine-facing but publicly served) - both in PENDING_OWNER_APPROVAL.
    // The service name ECHOES `seo.title` and moved with it 2026-09-22; a
    // JSON-LD name that still advertised the retired title would hand a crawler
    // the very string the title change was made to stop competing on.
    serviceName: "לידים לעורכי דין - קבלו פניות חמות | Leadify AI",
    serviceType: "לידים לעורכי דין, איתור לקוחות לעורכי דין, ניטור קבוצות פייסבוק",
    // The UNTRIMMED P23 / G12 description, kept verbatim: JSON-LD is not a SERP
    // snippet and carries no length cap, so the full approved sentence (brand
    // gloss and delivery promise included) belongs here rather than being cut
    // to match the meta tag.
    serviceDescription:
      "לידים לעורכי דין: סוכני AI של לידיפיי (Leadify AI) סורקים קבוצות פייסבוק 24/7, מזהים מי מחפש עורך דין בתחום ובאזור שלכם ושולחים לכם את ההזדמנות תוך שניות - למייל ולנייד.",
    // The H1's first line, which is also the page's head query.
    breadcrumbName: "לידים לעורכי דין",
  },

  hero: {
    // H1, owner's own wording (approved). Two rendered parts per line, exactly
    // like the homepage's H1.
    line1: ["לידים", "לעורכי דין"],
    line2: ["מקסימום תיקים.", "מינימום מאמץ."],
    // P4 / G2, approved verbatim. First brand mention on the page.
    sub: "סוכני ה-AI של לידיפיי (Leadify AI) קוראים את השיח בקבוצות פייסבוק וכל ליד, הזדמנות עסקית או לקוח פוטנציאלי שמחפש עורך דין בתחום ובאזור שלכם מגיע אליכם תוך שניות - אם מישהו מחפש עכשיו עורך דין. אתם תדעו על זה ראשונים.",
    // P5c - the owner's own wording, pending FINAL approval on the designed
    // page (hence its entry in PENDING_OWNER_APPROVAL).
    reassurance:
      "אנחנו לא מפרסמים ולא מגיבים בשמכם. אנחנו מאתרים את הפוסטים הרלוונטיים ומציגים לכם אותם כאילו נתקלתם בהם בעצמכם - ההחלטה והאחריות האתית האם ואיך להגיב נשארת שלכם בלבד.",
    // A18.4 / A18.5 / A18.6 - the homepage's own buttons and micro line.
    primaryCta: "הצטרף",
    secondaryCta: "התחבר",
    micro: "מתחילים בחינם תוך 60 שניות • ללא כרטיס אשראי",
    // A18.7 - the homepage's three stat chips, verbatim.
    stats: [
      { n: "+80h", l: "חיסכון חודשי" },
      { n: "24/7", l: "ניטור רציף" },
      { n: "500,000+", l: "פוסטים נסרקים בחודש" },
    ],
    leads: HERO_LEADS,
    fields: HERO_FIELDS,
  },

  // The benefits grid's three profession-bearing tiles (owner, 2026-09-22):
  // the push alert's first line, the four opening chips (the cycle adds
  // hero.fields) and the reply the AI-draft tile types out. New wording,
  // approved on the rendered page 2026-09-22.
  features: {
    pushAlert: "הזדמנות חמה חדשה - עורך דין תעבורה",
    // Four of HERO_FIELDS (the shorter names, so the chip row keeps its
    // shape); the cycle then deals the other four specialties.
    chips: ["עורך דין מקרקעין", "נוטריון", "עורך דין תעבורה", "עורך דין ביטוח לאומי"],
    // Answers the page's own traffic card (Haifa).
    draft:
      "היי, ראיתי שאתה מחפש עורך דין תעבורה באזור חיפה - אשמח לעזור, יש לי ניסיון של 12 שנה...",
  },

  where: {
    // Approved 2026-09-22 (SEO round 2), replacing P7 / G4's
    // "איפה מחפשים עורך דין". The eyebrow is the first line of the first prose
    // section, and this phrasing puts the visitor's own search intent
    // ("מחפשים לקוחות ל...") there instead of the reader's.
    eyebrow: "מחפשים לקוחות לעורכי דין?",
    // Owner's own wording (approved), replacing the article's A2 heading.
    h2: "איפה אנשים מחפשים היום עורכי דין",
    // A2.1 verbatim, minus its opening sentence (owner-approved drop,
    // 2026-09-17). The article's bold run is dropped: prose sections render
    // plain text.
    body: 'בשנת 2026 אלפי אנשים מחפשים עורכי דין ועצות משפטיות בקבוצות פייסבוק כל חודש: "מישהו מכיר עורכת דין לגירושין באזור המרכז?". מהניסיון שלנו בלידיפיי (Leadify AI), קבוצות פייסבוק בישראל מייצרות אלפי פוסטים בחודש של לקוחות פוטנציאליים שמחפשים עורך דין.',
  },

  opportunity: {
    // P8 / G4, approved.
    eyebrow: "איך נראית הזדמנות",
    // Approved 2026-09-22 (SEO round 2), replacing A23.4's
    // "מה כוללת כל הזדמנות שאקבל?". Before this the page carried the head query
    // in its H1 and in no H2 at all; this is the one H2 that repeats it, and it
    // sits on the section that shows the query's answer.
    h2: "לידים לעורכי דין - איך נראית הזדמנות אמיתית",
    // Two cards, as the document specifies - but the document's first one was
    // the homepage's labour-law lead, and `עו"ד דיני עבודה` has no catalogue
    // row, so it was dropped (one page may not mix naming styles) and the
    // traffic card took its place. Family first: it is the hotter score.
    cards: [
      {
        // P9 / G9, approved. The `pro` label is the CATALOGUE name
        // ("עורך דין משפחה וגירושין") rather than the document's
        // "עו\"ד דיני משפחה", which is not a catalogue entry - the swap is
        // listed in PENDING_OWNER_APPROVAL.
        pro: "עורך דין משפחה וגירושין",
        grp: "פורסם ב: אמהות בשרון",
        txt: "עברתי הליך גירושין ואני צריכה עורכת דין שתסביר לי מה קורה עכשיו עם הסכם הממון, אשמח להמלצות באזור.",
        city: "הרצליה",
        time: "לפני 4 דקות",
        score: 93,
      },
      {
        // Traffic - promoted out of the hero deck (foreman, 2026-09-17) so the
        // section shows two cards. Text is the document's own PROPOSED
        // decorative line (P-hero-sheet-3), verbatim. Second, and scored below
        // the family card, so the hot one leads.
        pro: "עורך דין תעבורה",
        grp: "פורסם ב: נהגים בצפון",
        txt: "קיבלתי דוח תנועה עם זימון לבית משפט, מישהו ממליץ על עורך דין תעבורה באזור חיפה?",
        city: "חיפה",
        time: "לפני 4 דקות",
        score: 92,
      },
    ],
    // A23.4 / A17.3 / A13.3, verbatim, bold runs dropped (plain-text render).
    body: [
      "תקבל הזדמנויות רלוונטיות בתחומים ובאזורים שתבחר - גם הזדמנויות גלויות וגם הזדמנויות סמויות שקל לפספס. לצד כל הזדמנות מצורף ניתוח AI מלא, ציון רלוונטיות, והודעת פנייה ראשונית מותאמת אישית מוכנה לשליחה. כך אתה לא רק מגלה את הלקוח הפוטנציאלי, אלא גם יודע בדיוק איך לפנות אליו.",
      "כל פוסט נקרא לפי משמעות וכוונה ולא לפי רשימת מילות מפתח שאתם מתחזקים. כל הזדמנות מגיעה עם דירוג חום ונימוק קצר שמסביר למה היא רלוונטית לכם, ואתם קובעים מאיזה דירוג לקבל התראה ואילו קבוצות לכלול.",
      "השירות לא כותב בשמכם ולא מגיב במקומכם, וזו הבחנה מהותית עבור עורך דין: התגובה, ההודעה הפרטית והאחריות האתית נשארות אצלכם במלואן. מה שאנחנו מביאים הוא הפוסט עצמו, לפני שהשרשור נסגר.",
    ],
  },

  // NEW - the note under the clients-logos heading (owner's English gist:
  // "companies that trust us in other areas as well").
  clientsNote: "חברות שסומכות עלינו - גם בתחומים אחרים",

  // NEW - the compact CTA band the owner asked to keep.
  ctaBand: {
    title: "הלקוח הבא שלכם כבר מחפש אתכם.",
    ctaLabel: "הצטרף עכשיו!",
  },

  cta: {
    // P19 / G6, approved.
    eyebrow: "מוכנים להתחיל?",
    // Owner's choice 2026-09-17, NEW wording.
    title: "מישהו מחפש עכשיו עורך דין. הצטרפו עכשיו ותהיו הראשונים לדעת.",
    // A24.3 verbatim.
    sub: "הצטרף עכשיו והתחל לקבל הזדמנויות כבר היום.",
    // A24.4 verbatim.
    ctaLabel: "הצטרף",
  },

  // ---------------------------------------------------------------------------
  // FAQ - twelve entries: the original ten (decision D3 applied) plus the two
  // query-shaped entries approved 2026-09-22 for SEO round 2.
  //
  // D3: the three transferred Bar-rules chapters (Q8/Q9/Q10, shortened variants
  // P16/P17/P18) REPLACE the article's overlapping short answers Q1/Q2/Q7.
  // D4: all three optional homepage questions (Q11/Q12/Q13) are in.
  //
  // ORDER (owner, 2026-09-17): the rules trio sits in the SECOND half - a sales
  // page opens on what the reader gets, not on regulation. It moves as a BLOCK
  // and keeps its internal order, because Q10's condensed answer says
  // "כל מה שפירטנו למעלה" and that only holds while Q8 and Q9 are above it -
  // at positions 7, 8, 9 they still are. The speed entry keeps position 4.
  //
  // Final order: SEO-1, Q3, Q5, Q6, SEO-2, speed, Q11, Q12, Q8, Q9, Q10, Q13.
  // The rules trio is now at positions 9, 10, 11 - still in the second half and
  // still with Q8 and Q9 above Q10, which is the only ordering constraint the
  // copy itself carries.
  //
  // `**bold**` and "\n" are the accordion's display markup and are stripped
  // before the FAQPage JSON-LD is built. English is present only where the
  // string already exists in Hebrew AND English on the homepage; the transferred
  // legal copy is Hebrew-only by design.
  // ---------------------------------------------------------------------------
  faq: [
    {
      // SEO round 2, approved 2026-09-22. The head query, phrased as the
      // question a searcher types, FIRST in the list: it is the entry Google
      // lifts into the FAQ rich result and the first thing a visitor who
      // scrolled here reads.
      q: { he: "איך משיגים לידים לעורכי דין בלי לקנות אותם?" },
      a: {
        he: "סוכני ה-AI סורקים קבוצות פייסבוק 24/7 ומזהים מי מחפש עורך דין בתחום ובאזור שלכם. כל לקוח פוטנציאלי מגיע אליכם עם הפוסט המקורי, ניתוח קצר ודירוג חום - בלי לקנות רשימות ובלי לשלם לכל ליד בנפרד.",
      },
    },
    {
      // Q3 - A15.3, verbatim.
      q: { he: "מה אסור לכתוב בתגובה בקבוצת פייסבוק?" },
      a: {
        he: "מחירים או ייעוץ ראשון ללא עלות, אחוזי הצלחה, הבטחת תוצאה וכל ניסוח שמזלזל בעמית או יוצר רושם מטעה. השאלה המנחה היא אם הקורא יוצא עם ציפייה שאי אפשר להבטיח לו.",
      },
    },
    {
      // Q5 - A15.5, verbatim.
      q: { he: "איך מגיבים לפוסט אנונימי על חובות או הוצאה לפועל?" },
      a: {
        he: "בתגובה פומבית בלבד, כי אין למי לפנות בפרטי. שמרו על ניסוח נטול שיפוט, הציעו צעד מעשי אחד והשאירו לשואל את הבחירה ליצור קשר. פרטים אישיים לעולם לא נכתבים בתגובה.",
      },
    },
    {
      // Q6 - A15.6, verbatim.
      q: { he: "האם עדיף לקנות לידים משפטיים או לאתר אותם בקבוצות?" },
      a: {
        he: "מחירונים פומביים של ספקי לידים מציגים פנייה משפטית בטווח שנע בין כמה עשרות שקלים לכ-150 שקלים, אך היא נמכרת לרוב לכמה משרדים. בקבוצה הפנייה פומבית ופתוחה לכל מי שעונה, והיתרון הוא המהירות.",
      },
    },
    {
      // SEO round 2, approved 2026-09-22. The second query shape. It sits
      // directly under Q6 on purpose: Q6 compares the two channels on price,
      // this one defines the two things being compared, and a reader who asked
      // the first question is already holding the second.
      q: { he: "מה ההבדל בין לידים לעורכי דין לבין לקוחות פוטנציאליים מקבוצות?" },
      a: {
        he: "ליד שנקנה הוא פרט קשר של מישהו שהתעניין פעם. לקוח פוטנציאלי שמזוהה בקבוצה הוא אדם שמחפש עורך דין ברגע זה, בפוסט שהוא כתב בעצמו - ולכן ההזדמנות העסקית שם חמה בהרבה.",
      },
    },
    {
      // Q4 (new) - the speed section, moved into the FAQ 2026-09-17. Question =
      // the article's own title (A9); answer = P14/A9.1 plus the A15.4 proof
      // line, verbatim, and nothing else.
      q: { he: "תוך כמה זמן צריך להגיב - ולמה זה קריטי?" },
      a: {
        he: "בתוך דקות עד שעות בודדות, לא ביום למחרת.\nמחקר סלואן מצא שפנייה תוך חמש דקות הגדילה **פי 21** את הסיכוי שהפנייה תתקדם לשלב המכירה.",
      },
    },
    {
      // Q11 - A23.3, verbatim from the homepage FAQ (he + en).
      q: {
        he: "איך לידיפיי עובדת ואילו קבוצות היא סורקת?",
        en: "How does Leadify work, and which groups does it scan?",
      },
      a: {
        he: "המערכת סורקת באופן קבוע מגוון קבוצות פייסבוק ציבוריות, ומנתחת **אלפי פוסטים ביום** בעזרת בינה מלאכותית כדי להבין מי באמת מחפש לקנות או לקבל שירות. בנוסף, עבור כל לקוח לידיפיי מאתרת אוטומטית את הקבוצות הרלוונטיות לתחום שלו ומוסיפה אותן לניטור - כדי שתגיע להזדמנויות העסקיות בזמן.",
        en: "Leadify regularly scans a range of public Facebook groups and analyzes **thousands of posts a day** with AI to understand who is genuinely looking to buy or hire. On top of that, for each customer it automatically finds the groups relevant to their field and adds them to monitoring - so you reach business opportunities in time.",
      },
    },
    {
      // Q12 - A23.5, verbatim from the homepage FAQ (he + en).
      q: {
        he: "כמה הזדמנויות אקבל בחודש?",
        en: "How many opportunities will I get per month?",
      },
      a: {
        he: "אנחנו לא יוצרים הזדמנויות יש מאין - הכמות תלויה בהיקף השיח שקיים בפועל בפייסבוק בתחום שלך ובאזור הפעילות שבחרת. מה שאנחנו כן מתחייבים לו: לסרוק ולנתח את השיח ברציפות, ולהביא אליך **כל הזדמנות עסקית רלוונטית שנאתר**. ככל שהשיח בתחומך ער יותר, כך תקבל יותר הזדמנויות.",
        en: "We don't create opportunities out of thin air - the volume depends on how much relevant conversation actually exists on Facebook in your field and chosen area. What we do commit to: continuously scanning and analyzing the conversation, and bringing you **every relevant business opportunity we identify**. The more active the conversation in your field, the more opportunities you'll get.",
      },
    },
    {
      // Q8 - question verbatim A3 (H2), answer P16 (approved shortened form).
      q: { he: "מה מותר לעורך דין לפרסם על פי כללי הלשכה?" },
      a: {
        he: 'פרסומת ברשתות חברתיות מותרת במפורש. כלל 3(א)(5) לכללי לשכת עורכי הדין (פרסומת), תשס"א-2001, מתיר פרסומת "באתרי אינטרנט, ברשתות חברתיות ובישומונים ייעודיים לעורכי דין", למעט אתרים פוגעניים, אתרים המיועדים לקטינים ופרסומת קופצת. לצדו יש גבולות שלא זזים: כלל 2א אוסר פרסומת שיש בה כדי להטעות את הציבור או לפגוע בו, או שפוגעת בכבוד המקצוע - גם כשהיא עומדת בכל שאר התנאים.\n**שכר טרחה** - כלל 3(ב)(14) מתיר מלל חופשי שמציע שירות משפטי, ובלבד שאין בו כל התייחסות לשכר טרחה.\n**רושם מטעה** - כלל 3(יא) מתיר המלצות, מכתבי תודה ודירוג במדריכי דירוג, ורק באופן שאינו יוצר רושם מטעה.\n**זלזול בעמית** - כלל 3(ד) אוסר סרטונים ותמונות שיש בהם כדי לגרום, במישרין או בעקיפין, לזלזול בעורך דין אחר.\n**רשימת תפוצה** - מידעון מותר לפי כלל 3(יג) רק למי שביקש לקבל אותו.\n**שמירת עותק** - כלל 3א מחייב לשמור שלוש שנים מיום הפסקת הפרסומת עותק שלה ורישום של מקומות הפרסום ומועדיו.\nזהו מידע כללי בלבד ולא ייעוץ משפטי או חוות דעת אתית. בכל ספק כדאי לפנות לוועדת האתיקה של הלשכה.',
      },
    },
    {
      // Q9 - question verbatim A4 (H2), answer P17 (approved shortened form).
      q: { he: "האם תגובה לפוסט שמחפש עורך דין נחשבת שידול?" },
      a: {
        he: 'התשובה הזהירה: לשון החוק והכללים אינה מכריעה את השאלה, ולכן הדרך הבטוחה היא להשיב מידע עניינית ולא לשכנע. סעיף 56 לחוק לשכת עורכי הדין, תשכ"א-1961, אוסר על עורך דין לשדל אדם למסור לידיו עבודה מקצועית - בעצמו או על ידי אחר. מנגד עומד כלל 5, שמתיר לעורך דין למסור מידע על משרדו, ניסיונו והתיקים שבהם היה מעורב - ללקוחותיו על פי פנייתם, ולאחרים הפונים אליו. מכאן נגזרת הקריאה הבטוחה, וכדאי לאמץ אותה: **מי שיזם את השיחה הוא הפונה, ואתם משיבים לו מידע** - לא משכנעים אותו למסור לכם תיק.',
      },
    },
    {
      // Q10 - question verbatim A5 (H2), answer P18 (approved shortened form).
      q: { he: "האם מותר לעורכי דין להשתמש בלידיפיי?" },
      a: {
        he: "כן. לידיפיי לא מפרסמת בשמכם, לא שולחת הודעות ולא מגיבה לאף פוסט - היא רק מודיעה לכם שמישהו מחפש עורך דין. התראה שמגיעה אליכם איננה אף אחד מהשניים: זה שירות שקורא בשבילכם את מה שכבר פורסם בפומבי, בדיוק כפי שהייתם קוראים אותו בעצמכם. לכן שאלת הכללים לא מתעוררת ברגע ההתראה, אלא ברגע שאתם כותבים תגובה - וכאן חל כל מה שפירטנו למעלה: להזדהות, לא לציין שכר טרחה, לא להבטיח תוצאה ולא לשדל.",
      },
    },
    {
      // Q13 - A23.11, verbatim from the homepage FAQ (he + en).
      q: {
        he: "כמה זמן לוקח להתחיל והאם צריך ידע טכני?",
        en: "How long does it take to start, and do I need technical knowledge?",
      },
      a: {
        he: "ההתחלה לוקחת **פחות מדקה** ולא דורשת **שום ידע טכני**. נרשמים, בוחרים תחום ואזורי פעילות - וכבר מתחילים לקבל הזדמנויות. אפשר להתחיל **בחינם, בלי כרטיס אשראי**.",
        en: "Getting started takes **less than a minute** and requires **no technical knowledge** at all. Sign up, choose your field and areas, and you'll start receiving opportunities. You can start **for free, no credit card required**.",
      },
    },
  ],

  // The blog bridge: the homepage's own three-card blog row, pinned to articles
  // a lawyer would actually open (owner, 2026-09-17 - cards, not link pills).
  // The section carries no copy of its own, so there is nothing here to approve.
  //
  //   1. the full lawyers guide - the page's own long-form counterpart, and
  //      where the four chapters cut from this page still live;
  //   2. the "mi mikir" reply playbook - the very act this page asks a lawyer to
  //      perform, answering a public recommendation request;
  //   3. hot vs cold leads - how to tell which of those requests is worth the
  //      first reply.
  //
  // Deliberately NOT the accountants / insurance guides: those are the sibling
  // professions' pages-to-be, not reading for this page's visitor.
  blogSlugs: [
    "leads-for-lawyers",
    "answering-recommendation-requests",
    "where-to-get-leads-israel",
  ],

  // ---------------------------------------------------------------------------
  // Everything on this page that the owner has not yet approved in its final
  // form, as the exact strings it renders. The page shows this list in
  // development so the owner can approve it in place.
  //
  // Each entry appears verbatim somewhere above - a test pins that, so an
  // approved string cannot quietly drift away from its approval record.
  // ---------------------------------------------------------------------------
  PENDING_OWNER_APPROVAL: [
    // Hero reassurance line (P5c) - the owner's own wording, pending final
    // approval on the designed page.
    "אנחנו לא מפרסמים ולא מגיבים בשמכם. אנחנו מאתרים את הפוסטים הרלוונטיים ומציגים לכם אותם כאילו נתקלתם בהם בעצמכם - ההחלטה והאחריות האתית האם ואיך להגיב נשארת שלכם בלבד.",

    // Closing CTA heading (owner's choice, new wording).
    "מישהו מחפש עכשיו עורך דין. הצטרפו עכשיו ותהיו הראשונים לדעת.",

    // CTA band (new).
    "הלקוח הבא שלכם כבר מחפש אתכם.",
    "הצטרף עכשיו!",

    // Clients-strip note (new).
    "חברות שסומכות עלינו - גם בתחומים אחרים",

    // Hero demo deck - the invented posts (the traffic one now sits in the
    // opportunity section).
    "נפצעתי בתאונת עבודה לפני חודש וחברת הביטוח דוחה את התביעה שלי, מישהו ממליץ על עורך דין נזיקין באזור?",
    "הוועדה הרפואית של הביטוח הלאומי קבעה לי אחוזי נכות נמוכים מדי ואני רוצה לערער, אשמח להמלצה על עורך דין.",
    "אנחנו קונים דירה ראשונה מקבלן וצריכים עורך דין מקרקעין שיעבור על חוזה הרכישה לפני החתימה, המלצות באזור?",
    "אמא שלי בת 82 ורוצה לערוך ייפוי כוח מתמשך בזמן שהיא צלולה, מישהו מכיר עורך דין שמוסמך לזה באזור?",
    'אני צריכה תרגום נוטריוני של תעודת לידה ותעודת בגרות להגשה ללימודים בחו"ל, מישהו ממליץ על נוטריון באזור?',
    "קיבלתי דוח תנועה עם זימון לבית משפט, מישהו ממליץ על עורך דין תעבורה באזור חיפה?",
    "נכנסתי לחובות אחרי שהעסק שלי נסגר ואני שוקל הליך חדלות פירעון, מישהו מכיר עורך דין שילווה אותי בתהליך?",

    // The invented group names those demo posts are shown in.
    "פורסם ב: תושבי ראשון לציון והסביבה",
    "פורסם ב: המלצות בחיפה והקריות",
    "פורסם ב: קונים דירה במרכז",
    "פורסם ב: מטפלים בהורים מבוגרים",
    'פורסם ב: לומדים ועובדים בחו"ל',
    "פורסם ב: נהגים בצפון",
    "פורסם ב: כלכלת המשפחה - עוזרים אחד לשני",

    // The approved family-law demo card (P9) carries the CATALOGUE profession
    // name instead of the document's "עו\"ד דיני משפחה", which is not a
    // catalogue entry. Its group name is from the document; the card's own
    // "פורסם ב:" prefix makes the rendered string a new one.
    "עורך דין משפחה וגירושין",
    "פורסם ב: אמהות בשרון",

    // The other profession labels this page names in public - on the cards and
    // in the mesh field deck. Each is a catalogue name (see the verification
    // note on HERO_FIELDS), but none of them appears in the approval document,
    // so the owner approves the LIST as page copy.
    "עורך דין נזיקין וביטוח",
    "עורך דין ביטוח לאומי",
    "עורך דין מקרקעין",
    "עורך דין ייפוי כוח מתמשך",
    "נוטריון",
    "עורך דין תעבורה",
    "עורך דין חדלות פירעון",

    // JSON-LD strings (machine-facing, publicly served).
    "לידים לעורכי דין - קבלו פניות חמות | Leadify AI",
    "לידים לעורכי דין, איתור לקוחות לעורכי דין, ניטור קבוצות פייסבוק",
  ],
};

// Validated ONCE, at import, and only outside production: jest is the real gate
// (professionContentSchema.test.js), this is the fast local feedback for anyone
// editing the module, and a production bundle should not pay for a walk over
// every string on every boot.
if (process.env.NODE_ENV !== "production") {
  assertProfessionContent(content);
}

export default content;
